// JSON schemas for POST /api/transcription/structured, one per AI-interview
// section (see AI_SECTIONS in MotorClaimForm.vue). Each targets exactly the
// wizard-state (S) fields claimStepMapper.js's to*Body functions already
// read — not the unrelated flat keys in claim-form-fields.js, which belong
// to the separate public form path.
//
// Enum values are duplicated from the step components that own each field
// (WhatHappenedStep.vue's SURFACES/WEATHER/VIS/LIGHT, DamageStep.vue's
// AREAS/SEVERITY, PeopleStep.vue's ROLES/INJURY) rather than imported, since
// those consts aren't exported from the SFCs. If a step component's option
// list changes, update the matching schema here too.

export const DEFAULT_EXTRACTION_PROMPT =
  'Extract only what this transcript explicitly states. Leave a field unset if it is not mentioned — never guess or infer a value.'

const SURFACES = ['Dry', 'Wet', 'Loose gravel', 'Potholed', 'Muddy']
const WEATHER = ['Clear', 'Light rain', 'Heavy rain', 'Fog', 'Dusty']
const VIS = ['Good', 'Moderate', 'Poor']
const LIGHT = ['Daylight', 'Dusk or dawn', 'Street lights', 'No lights']
const ROLES = [
  'Passenger in my vehicle',
  'Passenger in another vehicle',
  'Pedestrian',
  'Cyclist / boda rider',
  'Other driver',
  'Other',
]
const INJURY = ['Not injured', 'Injured', 'Injury unknown']
const AREAS = [
  'Front bumper',
  'Bonnet',
  'Windscreen',
  'Right headlight',
  'Left headlight',
  'Right side',
  'Left side',
  'Rear bumper',
  'Boot / tailgate',
  'Roof',
  'Wheels',
  'Undercarriage',
]
const SEVERITY = ['Minor', 'Moderate', 'Severe']

export const DRIVER_SCHEMA = {
  type: 'object',
  properties: {
    driverWasMe: { type: 'boolean', description: 'Whether the person speaking / insured was driving (true) or someone else was driving (false)',},
    otherDriverName: { type: 'string', description: 'Full name of the driver if someone else was driving',},
    driverEmail: { type: 'string', description: 'Email address of the driver if the speaker/insured was driving',},
    otherDriverEmail: { type: 'string', description: 'Email address of the driver if someone else was driving', },
    driverToBlame: { type: 'boolean', description: 'Whether the driver was to blame for the accident (true if to blame, false if not to blame)',},
  },
}

export const STORY_SCHEMA = {
  type: 'object',
  properties: {
    locationName: {
      type: 'string',
      description: 'Where the accident happened — a place, road or landmark',
    },
    surface: { type: 'string', enum: SURFACES },
    weather: { type: 'string', enum: WEATHER },
    vis: { type: 'string', enum: VIS, description: 'Visibility at the time' },
    light: { type: 'string', enum: LIGHT, description: 'Lighting conditions' },
  },
}

export const PEOPLE_SCHEMA = {
  type: 'object',
  properties: {
    people: {
      type: 'array',
      description: 'One entry per passenger mentioned',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          age: { type: 'string' },
          role: { type: 'string', enum: ROLES },
          injured: { type: 'string', enum: INJURY },
          hospital: { type: 'string', description: 'Hospital, if mentioned' },
        },
      },
    },
  },
}

export const OTHER_SCHEMA = {
  type: 'object',
  properties: {
    vehicles: {
      type: 'array',
      description: 'One entry per other vehicle involved',
      items: {
        type: 'object',
        properties: {
          reg: { type: 'string', description: 'Registration number' },
          makeModel: { type: 'string' },
          owner: { type: 'string' },
          phone: { type: 'string' },
          insurer: { type: 'string' },
          damage: { type: 'string', description: 'Damage to this vehicle' },
        },
      },
    },
    otherProperty: {
      type: 'string',
      description: 'Other property damaged (fence, building, etc.), if any',
    },
  },
}

export const WITNESS_SCHEMA = {
  type: 'object',
  properties: {
    witnessName: { type: 'string' },
    witnessPhone: { type: 'string' },
    police: {
      type: 'boolean',
      description: 'Whether police attended the scene',
    },
    station: { type: 'string', description: 'Police station, if mentioned' },
    ob: {
      type: 'string',
      description: 'OB / occurrence book or constable number, if mentioned',
    },
    policeTel: {
      type: 'string',
      description: 'Police telephone number, if mentioned',
    },
    trafficOffenceNumber: {
      type: 'string',
      description: 'Traffic offence number, if mentioned',
    },
  },
}

export const DAMAGE_SCHEMA = {
  type: 'object',
  properties: {
    areas: {
      type: 'array',
      description: 'Damaged parts mentioned',
      items: { type: 'string', enum: AREAS },
    },
    severity: { type: 'string', enum: SEVERITY },
    preex: {
      type: 'string',
      description: 'Pre-existing damage mentioned, if any',
    },
  },
}

// Keyed by AI_SECTIONS' section key.
export const EXTRACTION_SCHEMAS = {
  driver: DRIVER_SCHEMA,
  story: STORY_SCHEMA,
  people: PEOPLE_SCHEMA,
  other: OTHER_SCHEMA,
  witness: WITNESS_SCHEMA,
  damage: DAMAGE_SCHEMA,
}
